import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/**
 * Original VYV editorial draft for explicitly declared product variants.
 * This is static instruction, not observation or personalized assessment.
 * Reference coverage and adaptations:
 * docs/research/fitness-rea-20261015/core-variant-guidance.md
 */
export const EXTENDED_EXERCISE_GUIDANCE_IDS = [
  'reverse-crunch', 'supine-leg-raise', 'dead-bug', 'bicycle-crunch',
  'cross-body-oblique-crunch', 'seated-russian-twist', 'mountain-climber', 'kneeling-ab-wheel',
] as const;
export type ExtendedExerciseGuidanceId = typeof EXTENDED_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type ExtendedExerciseGuidanceDictionary = Record<ExtendedExerciseGuidanceId, GuidanceCopy>;

const supported = new Set<string>(EXTENDED_EXERCISE_GUIDANCE_IDS);

/** Exact app identity/canonical name only; never infer an external variant from display text. */
export function getExtendedExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  if (!id || !supported.has(id)) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'extended', id);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
